import { FiPause, FiPlay } from 'react-icons/fi';
import { useEffect, useRef, useState, type JSX } from 'react';
import styles from './VideoSlideContent.module.css';
import type { VideoSlideContentProps } from './VideoSlideContent.types';

export const VideoSlideContent = ({
  src,
  isCurrent,
  onPlayStateChange,
}: VideoSlideContentProps): JSX.Element => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showPlayStateIndicator, setShowPlayStateIndicator] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isCurrent) {
      video.play().catch((err) => {
        console.warn('Auto-play failed:', err);
      });
      setIsPlaying(true);
    } else {
      video.pause();
      video.currentTime = 0;
      setIsPlaying(false);
      setShowPlayStateIndicator(false);
    }
  }, [isCurrent]);

  const handleTogglePlay = (): void => {
    const video = videoRef.current;
    if (!video) return;

    let nextPlayingState = false;
    if (video.paused) {
      video.play().catch((err) => console.warn(err));
      nextPlayingState = true;
    } else {
      video.pause();
      nextPlayingState = false;
    }

    setIsPlaying(nextPlayingState);
    setShowPlayStateIndicator(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setShowPlayStateIndicator(false);
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <video
        loop
        playsInline
        ref={videoRef}
        src={src}
        className={styles.fullScreenImage}
        onClick={handleTogglePlay}
        onPlay={() => onPlayStateChange?.(true)}
        onPause={() => onPlayStateChange?.(false)}
      />
      {showPlayStateIndicator && (
        <div className={styles.playStateIndicator}>
          {isPlaying ? <FiPlay size={48} /> : <FiPause size={48} />}
        </div>
      )}
    </div>
  );
};
